import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrenciesStore } from '@app/ui/stores';
import { ReportType, type ReportEntity } from '@app/domain/entities';
import { transformReportAmounts } from '../report-viewer.utils';
import {
  loadExcelJs,
  sanitizeSheetName,
  sanitizeFilename,
  dateCellNumFmt,
  currencyNumFmt,
  addFormattedRow,
  setColumnWidths,
} from './report-export.utils';
import { REPORT_EXPORT_FILE_SERVICE, type ReportExportFileService } from './report-export-file.service';
import type { IReportExportUseCase } from './report-export.use-case';

interface AccountBalancesReportData {
  options: {
    groupBy: 'day' | 'month' | 'year';
  };
  data: {
    balances: Array<{
      date_part: number;
      accountName: string;
      accountPosition: number;
      amounts: Record<string, number>;
    }>;
  };
}

@Injectable({ providedIn: 'root' })
export class ExportAccountBalancesUseCase implements IReportExportUseCase {
  readonly reportType = ReportType.ACCOUNT_BALANCES;

  private readonly translate = inject(TranslateService);
  private readonly currenciesStore = inject(CurrenciesStore);
  private readonly reportExportFileService = inject<ReportExportFileService>(REPORT_EXPORT_FILE_SERVICE);

  async export(report: ReportEntity): Promise<void> {
    if (!report.data) return;

    const data = report.data as AccountBalancesReportData;
    const ExcelJS = await loadExcelJs();
    const workbook = new ExcelJS.Workbook();

    const currencies = this.currenciesStore.currencies();
    const { transformAmounts } = transformReportAmounts(data.data.balances, currencies);

    const allCurrencies = transformAmounts({}).map((a) => a.currency);
    const columnFormats: (string | undefined)[] = [
      dateCellNumFmt(data.options.groupBy),
      undefined, // account name column — no number format
      ...allCurrencies.map((c) => currencyNumFmt(c.code)),
    ];

    const title = this.translate.instant('screens.reports.types.account_balances') as string;
    const sheet = workbook.addWorksheet(sanitizeSheetName(title));

    const dateHeader = this.translate.instant('common.labels.date') as string;
    const accountHeader = this.translate.instant('common.labels.account') as string;
    sheet.addRow([dateHeader, accountHeader, ...allCurrencies.map((c) => c.name)]);
    setColumnWidths(sheet, ['date', 'text', ...allCurrencies.map(() => 'currency' as const)]);

    for (const item of data.data.balances) {
      addFormattedRow(
        sheet,
        [new Date(item.date_part * 1000), item.accountName, ...transformAmounts(item.amounts).map((a) => a.value ?? 0)],
        columnFormats,
      );
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const typeName = this.translate.instant(`screens.reports.types.${report.type.toLowerCase()}`) as string;
    await this.reportExportFileService.saveExcelFile(
      buffer,
      `${sanitizeFilename(typeName)}-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }
}
